// The DOM half of the replay VHS look (§12.8 item 4, §11.2): a camcorder-style tag, a tape counter
// (bottom-left; the UI's skip pill owns bottom-right, INTEGRATION.md), the title card that slams in during the rewind wipe ("INSTANT REPLAY", "BONK CAM",
// "PLAY OF THE MATCH") and, in BONK CAM, the "BONKED BY <name>" label in the killer's identity color.
// The shader half (scanlines, chroma split, tracking wobble, rewind tears) is the FinalPass.
// Mounted by the Renderer right above the game canvas, below the UI layer; pointer-events none, and
// `display: none` outside replays so its blended scanline layer costs nothing in play.
import type { ReplayKind } from '../../game/FrameView';

const STYLE_ID = 'vhs-overlay-style';
const CSS = `
.vhs-overlay { position: fixed; inset: 0; pointer-events: none; overflow: hidden; z-index: 0; display: none;
  font-family: 'Lilita One', 'Arial Rounded MT Bold', system-ui, sans-serif; color: #fff; }
.vhs-overlay.on { display: block; }
.vhs-overlay .vhs-tag { position: absolute; top: calc(max(10px, env(safe-area-inset-top)) + clamp(44px, 9vmin, 72px)); left: 50%; transform: translateX(-50%);
  font-size: clamp(16px, 3.4vmin, 30px); white-space: nowrap; letter-spacing: 0.06em; text-shadow: 3px 3px 0 #2A1A3A, -1px -1px 0 #2A1A3A, 1px -1px 0 #2A1A3A, -1px 1px 0 #2A1A3A;
  display: flex; align-items: center; gap: 0.45em; }
.vhs-overlay .vhs-dot { width: 0.62em; height: 0.62em; border-radius: 50%; background: #FF4D4D; box-shadow: 0 0 0 3px #2A1A3A; animation: vhs-blink 1s steps(2, start) infinite; }
.vhs-overlay.rewind .vhs-dot { display: none; }
.vhs-overlay .vhs-counter { position: absolute; bottom: max(18px, env(safe-area-inset-bottom)); left: max(24px, env(safe-area-inset-left));
  font-family: 'Courier New', ui-monospace, monospace; font-weight: 700; font-size: clamp(12px, 2.4vmin, 22px);
  text-shadow: 2px 2px 0 #2A1A3A; letter-spacing: 0.08em; opacity: 0.92; }
.vhs-overlay .vhs-title { position: absolute; left: 50%; top: 40%; transform: translate(-50%, -50%) rotate(-3deg) scale(0.6); opacity: 0;
  font-size: clamp(28px, 9vmin, 92px); line-height: 1; padding: 0.18em 0.5em 0.24em; border: 0.08em solid #2A1A3A; border-radius: 0.26em;
  background: #FF4F9A; box-shadow: 0 0.12em 0 #2A1A3A; white-space: nowrap; color: #FFF6E9;
  text-shadow: 0 0.06em 0 #2A1A3A; transition: transform 180ms cubic-bezier(.2,1.6,.4,1), opacity 120ms linear; }
.vhs-overlay[data-kind="bonkcam"] .vhs-title { background: #FF4D4D; top: calc(max(10px, env(safe-area-inset-top)) + clamp(64px, 15vmin, 120px)); font-size: clamp(22px, 6vmin, 60px); }
.vhs-overlay[data-kind="bonkcam"].title .vhs-tag { opacity: 0; }
.vhs-overlay[data-kind="potm"] .vhs-title { background: #FFC93C; color: #2A1A3A; text-shadow: none; }
.vhs-overlay.title .vhs-title { opacity: 1; transform: translate(-50%, -50%) rotate(-3deg) scale(1); }
.vhs-overlay .vhs-killer { position: absolute; left: 50%; bottom: 14%; transform: translateX(-50%) rotate(-1.5deg); opacity: 0;
  font-size: clamp(17px, 4.4vmin, 42px); padding: 0.16em 0.6em 0.22em; border-radius: 0.4em; border: 0.09em solid #2A1A3A;
  background: #FFF6E9; color: #2A1A3A; box-shadow: 0 0.14em 0 #2A1A3A; white-space: nowrap; transition: opacity 150ms linear; }
.vhs-overlay .vhs-killer b { font-weight: 400; color: var(--killer, #FF5A4E); -webkit-text-stroke: 0.05em #2A1A3A; paint-order: stroke fill; }
.vhs-overlay.killer .vhs-killer { opacity: 1; }
.vhs-overlay .vhs-lines { position: absolute; inset: 0; background: repeating-linear-gradient(180deg, rgba(255,255,255,0.05) 0 2px, rgba(0,0,0,0.05) 2px 4px); mix-blend-mode: overlay; }
@keyframes vhs-blink { 0% { opacity: 1; } 100% { opacity: 0; } }
@media (prefers-reduced-motion: reduce) { .vhs-overlay .vhs-dot { animation: none; } }
`;

const TITLES: Readonly<Record<ReplayKind, string>> = { instant: 'INSTANT REPLAY', bonkcam: 'BONK CAM', potm: 'PLAY OF THE MATCH' };
const TAGS: Readonly<Record<ReplayKind, string>> = { instant: 'REPLAY', bonkcam: 'BONK CAM', potm: 'PLAY OF THE MATCH' };
/** Seconds of playback the title card stays up after the wipe. */
const TITLE_HOLD_SEC: Readonly<Record<ReplayKind, number>> = { instant: 0.6, bonkcam: 0.9, potm: 0.6 };

export interface VhsState {
  kind: ReplayKind;
  /** Replay playback seconds; negative during the rewind wipe. */
  t: number;
  duration: number;
  /** BONK CAM killer label, or null. */
  killer: { name: string; hex: string } | null;
}

export class VhsOverlay {
  readonly el: HTMLDivElement;
  private readonly tag: HTMLSpanElement;
  private readonly counter: HTMLDivElement;
  private readonly title: HTMLDivElement;
  private readonly killer: HTMLDivElement;
  private shown = false;
  private lastKey = '';

  constructor(parent: HTMLElement, before: Element | null) {
    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent = CSS;
      document.head.append(style);
    }
    const el = document.createElement('div');
    el.className = 'vhs-overlay';
    el.id = 'vhs-overlay';
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML = '<div class="vhs-lines"></div><div class="vhs-tag"><span class="vhs-dot"></span><span class="vhs-text"></span></div>'
      + '<div class="vhs-counter"></div><div class="vhs-title"></div><div class="vhs-killer"></div>';
    parent.insertBefore(el, before);
    this.el = el;
    this.tag = el.querySelector('.vhs-text') as HTMLSpanElement;
    this.counter = el.querySelector('.vhs-counter') as HTMLDivElement;
    this.title = el.querySelector('.vhs-title') as HTMLDivElement;
    this.killer = el.querySelector('.vhs-killer') as HTMLDivElement;
  }

  get visible(): boolean {
    return this.shown;
  }

  /** Shows the overlay for `s`, or hides it (null). Cheap to call every frame. */
  update(s: VhsState | null): void {
    if (!s) {
      if (this.shown) {
        this.shown = false;
        this.el.className = 'vhs-overlay';
        this.lastKey = '';
      }
      return;
    }
    this.shown = true;
    const rewinding = s.t < 0;
    // The title card holds through the wipe and the first moments of play (0.6 s). BONK CAM's hit
    // comes right at the start of its clip, so its card is smaller and sits up top, in place of the
    // camcorder tag, clear of the action.
    const title = s.t < TITLE_HOLD_SEC[s.kind];
    const killer = s.killer !== null && s.t > 0.15;
    const cls = `vhs-overlay on${rewinding ? ' rewind' : ''}${title ? ' title' : ''}${killer ? ' killer' : ''}`;
    if (this.el.className !== cls) this.el.className = cls;
    if (this.el.dataset.kind !== s.kind) this.el.dataset.kind = s.kind;
    const key = `${s.kind}|${rewinding ? 1 : 0}|${s.killer?.name ?? ''}`;
    if (key !== this.lastKey) {
      this.lastKey = key;
      this.tag.textContent = rewinding ? '◀◀ REWIND' : `▶ ${TAGS[s.kind]}`;
      this.title.textContent = TITLES[s.kind];
      if (s.killer) {
        this.killer.style.setProperty('--killer', s.killer.hex);
        this.killer.innerHTML = '';
        this.killer.append('BONKED BY ');
        const b = document.createElement('b');
        b.textContent = s.killer.name.toUpperCase();
        this.killer.append(b);
      }
    }
    const t = Math.max(0, s.t);
    const frames = Math.floor((t * 30) % 30);
    const secs = Math.floor(t);
    const text = `SP 0:${String(secs).padStart(2, '0')}:${String(frames).padStart(2, '0')}`;
    if (this.counter.textContent !== text) this.counter.textContent = text;
  }

  dispose(): void {
    this.el.remove();
  }
}
